import { Group, Stack, Text, useMantineTheme } from '@mantine/core';
import { useForm } from '@mantine/form';

import { useTranslation } from 'react-i18next';

import { Button } from '../../../../../shared/components/UI/Button/Button';
import { TextArea } from '../../../../../shared/components/UI/TextArea/TextArea';

import { GradientBorder } from '../../../../../shared/components/GradientBorder';
import { useCreateVaultSearchMutation } from '../hooks/vaultSearch.hook';

type FormValues = { question: string };

export function VaultSearchInput({
  withExample = false,
}: {
  withExample?: boolean;
}) {
  const createSearchMutation = useCreateVaultSearchMutation({
    onSuccess: () => {
      form.reset();
    },
  });
  const { t } = useTranslation(['bidResponse', 'common']);

  const theme = useMantineTheme();

  const form = useForm<FormValues>({
    initialValues: {
      question: '',
    },
    validate: {
      question: (value: string) =>
        value.length > 0 ? null : t('common:fieldRequired'),
    },
  });

  const onSubmit = async (values: FormValues) => {
    createSearchMutation.mutate({ question: values.question });
  };

  const handleExampleClick = (example: string) => {
    form.setFieldValue('question', example);
    onSubmit({ question: example });
  };

  return (
    <Stack
      spacing="04"
      style={{
        flexGrow: 1,
      }}
    >
      <form onSubmit={form.onSubmit(onSubmit)}>
        <GradientBorder
          borderWidth={1.6}
          borderRadius={theme.radius.lg}
          gradient={theme.other.gradients.background1}
          wrapperProps={{
            w: '100%',
            maw: '926px',
            sx: {
              boxShadow: '0px 67.5px 101.25px 0px rgba(25, 64, 154, 0.06)',
            },
          }}
          contentProps={{
            w: '100%',
          }}
        >
          <Stack w="100%" spacing={0} p="04" pt="02">
            <TextArea
              w="100%"
              variant="white"
              size="xs"
              fw={400}
              fs="18px"
              maxRows={10}
              autoFocus
              disabled={createSearchMutation.isPending}
              placeholder="Rechercher dans mon coffre fort de documents..."
              {...form.getInputProps('question')}
            />
            <Group noWrap position="right" pl="01">
              <Button
                size="md"
                disabled={!form.values.question.trim()}
                onClick={() => form.onSubmit(onSubmit)()}
              >
                {t('search.newSearchInput.submitButton')}
              </Button>
            </Group>
          </Stack>
        </GradientBorder>
      </form>

      {withExample && <ExampleList onExampleClick={handleExampleClick} />}
    </Stack>
  );
}

function ExampleList({
  onExampleClick,
}: {
  onExampleClick: (example: string) => void;
}) {
  const { t } = useTranslation('bidResponse');

  const examples = t('search.exampleQuestions', {
    returnObjects: true,
  });

  return (
    <Stack spacing="02">
      <Text c="gray.5" size="sm">
        {t('search.forExample')} :
      </Text>
      {examples.map(example => (
        <ExampleItem key={example} onClick={onExampleClick} value={example} />
      ))}
    </Stack>
  );
}

function ExampleItem({
  onClick,
  value,
}: {
  onClick: (value: string) => void;
  value: string;
}) {
  function handleClick() {
    onClick(value);
  }

  return (
    <Text
      c="primary.7"
      size="xs"
      onClick={handleClick}
      sx={theme => ({
        transition: 'color 0.2s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          color: theme.colors.blue[5],
        },
      })}
    >
      {value}
    </Text>
  );
}
